import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Pencil, Trash2, Loader2, ImageIcon } from "lucide-react";
import { api } from "@/lib/api";
import { fmtMoney, CATEGORY_COLORS } from "@/lib/theme";
import { ReceiptFields } from "@/components/ReceiptFields";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

function b64ToDataUrl(b64) {
  if (!b64) return "";
  if (b64.startsWith("data:")) return b64;
  const mime = b64.startsWith("iVBORw0KGgo") ? "image/png" : "image/jpeg";
  return `data:${mime};base64,${b64}`;
}

export default function ReceiptDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [receipt, setReceipt] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = useCallback(async () => {
    try {
      const r = await api(`/receipts/${id}`);
      setReceipt(r);
      setForm({
        vendor: r.vendor,
        amount: String(r.amount),
        receipt_date: r.receipt_date,
        category: r.category,
        notes: r.notes || "",
        gallons: r.gallons != null ? String(r.gallons) : "",
        state: r.state || "",
        gps_label: r.gps_label || "",
      });
    } catch (e) {
      if (e?.status === 404) setNotFound(true);
      else toast.error(e?.message || "Couldn't load receipt.");
    }
  }, [id]);

  useEffect(() => {
    document.title = "Receipt · RigBooks";
    load();
  }, [load]);

  const save = async () => {
    if (!form.vendor.trim()) return toast.error("Vendor is required.");
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) return toast.error("Enter a valid amount.");
    setSaving(true);
    try {
      const updated = await api(`/receipts/${id}`, {
        method: "PUT",
        body: {
          vendor: form.vendor.trim(),
          amount,
          receipt_date: form.receipt_date,
          category: form.category,
          notes: form.notes || "",
          gps_label: form.gps_label || "",
          gallons: form.category === "Fuel" && form.gallons ? parseFloat(form.gallons) : null,
          state: form.category === "Fuel" ? form.state || "" : "",
        },
      });
      setReceipt(updated);
      setEditing(false);
      toast.success("Receipt updated.");
    } catch (e) {
      toast.error(e?.message || "Update failed.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await api(`/receipts/${id}`, { method: "DELETE" });
      toast.success("Receipt deleted.");
      navigate("/receipts");
    } catch (e) {
      toast.error(e?.message || "Delete failed.");
    }
  };

  if (notFound) {
    return (
      <div className="flex flex-col items-center rounded-2xl border border-rig-border bg-rig-surface p-14 text-center">
        <p className="font-display text-2xl uppercase tracking-wide text-white">Receipt not found</p>
        <Button onClick={() => navigate("/receipts")} className="mt-5 bg-rig-accent text-rig-accentText">
          Back to receipts
        </Button>
      </div>
    );
  }

  if (!receipt || !form) {
    return (
      <div className="grid gap-6 lg:grid-cols-2">
        <Skeleton className="h-80 bg-rig-surface" />
        <Skeleton className="h-80 bg-rig-surface" />
      </div>
    );
  }

  const img = b64ToDataUrl(receipt.image_base64);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button
          variant="ghost"
          onClick={() => navigate("/receipts")}
          className="text-rig-dim hover:text-white"
          data-testid="receipt-back-btn"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> All receipts
        </Button>
        <div className="flex gap-2">
          {!editing && (
            <Button
              variant="outline"
              onClick={() => setEditing(true)}
              data-testid="edit-receipt-btn"
              className="border-rig-borderStrong bg-rig-surface text-white hover:bg-rig-surface2"
            >
              <Pencil className="mr-2 h-4 w-4" /> Edit
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => setConfirmOpen(true)}
            data-testid="delete-receipt-btn"
            className="border-rig-error/50 bg-transparent text-rig-error hover:bg-rig-error/10"
          >
            <Trash2 className="mr-2 h-4 w-4" /> Delete
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-rig-border bg-rig-surface p-4">
          {img ? (
            <img src={img} alt={receipt.vendor} className="w-full rounded-xl object-contain" />
          ) : (
            <div className="flex min-h-[300px] flex-col items-center justify-center text-center">
              <ImageIcon className="h-12 w-12 text-rig-mute" />
              <p className="mt-3 text-sm text-rig-dim">No image attached to this receipt.</p>
            </div>
          )}
        </div>

        <div>
          {!editing ? (
            <div className="rounded-2xl border border-rig-border bg-rig-surface p-6">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display text-3xl font-bold text-white">{receipt.vendor}</div>
                  <div className="mt-1 text-sm text-rig-dim">{receipt.receipt_date}</div>
                </div>
                <div className="font-display text-4xl font-bold text-rig-accent">{fmtMoney(receipt.amount)}</div>
              </div>
              <span
                className="mt-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold"
                style={{
                  background: `${CATEGORY_COLORS[receipt.category] || "#94A3B8"}22`,
                  color: CATEGORY_COLORS[receipt.category] || "#94A3B8",
                }}
              >
                {receipt.category}
              </span>

              <dl className="mt-6 space-y-3 border-t border-rig-border pt-5 text-sm">
                {receipt.gps_label && (
                  <div className="flex justify-between">
                    <dt className="text-rig-dim">Location</dt>
                    <dd className="text-white">{receipt.gps_label}</dd>
                  </div>
                )}
                {receipt.category === "Fuel" && (
                  <>
                    <div className="flex justify-between">
                      <dt className="text-rig-dim">Gallons</dt>
                      <dd className="text-white">{receipt.gallons != null ? receipt.gallons : "—"}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-rig-dim">State (IFTA)</dt>
                      <dd className="text-white">{receipt.state || "—"}</dd>
                    </div>
                  </>
                )}
                {receipt.notes && (
                  <div>
                    <dt className="text-rig-dim">Notes</dt>
                    <dd className="mt-1 text-white">{receipt.notes}</dd>
                  </div>
                )}
              </dl>
            </div>
          ) : (
            <div className="rounded-2xl border border-rig-border bg-rig-surface p-6">
              <ReceiptFields form={form} set={set} />
              <div className="mt-6 flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setEditing(false);
                    load();
                  }}
                  className="h-12 flex-1 border-rig-borderStrong bg-transparent text-white hover:bg-rig-surface2"
                >
                  Cancel
                </Button>
                <Button
                  onClick={save}
                  disabled={saving}
                  data-testid="save-edit-btn"
                  className="h-12 flex-[2] bg-rig-accent font-display text-sm font-bold uppercase tracking-widest text-rig-accentText hover:bg-rig-accentDark"
                >
                  {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save Changes"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Delete this receipt?"
        description="This removes it from your audit archive permanently."
        confirmLabel="Delete"
        destructive
        onConfirm={remove}
        testID="delete-receipt-confirm"
      />
    </div>
  );
}
